import classNames from "classnames";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import {
  MdFlag,
  MdFlashOn,
  MdPhone,
  MdPhoneMissed,
  MdHeadset,
} from "react-icons/md";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACTIVITY_TYPES,
  CALL_DIRECTIONS,
  CALL_STATUSES,
  SUBSCRIPTION_STATUSES,
  TYPES,
} from "utils/constants";
import { CALL_RECORDING_URL } from "utils/api/graphql/queries/call";
import { formatDate } from "utils/helpers/date";

const mapStatus = (status, TRANSLATION) => {
  const values = {
    [SUBSCRIPTION_STATUSES.DRAFT]: {
      label: TRANSLATION.STATUS.DRAFT,
      icon: <MdFlashOn size={12} />,
    },
    [SUBSCRIPTION_STATUSES.PENDING]: {
      label: TRANSLATION.STATUS.SEND,
      icon: <MdFlashOn size={12} />,
    },
    [SUBSCRIPTION_STATUSES.SIGNED]: {
      label: TRANSLATION.STATUS.SIGNED,
      icon: <MdFlashOn size={12} />,
    },
    DEFAULT: {
      label: status,
      icon: <MdFlag size={12} />,
    },
  };
  return values[status] || values.DEFAULT;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}min ${String(secs).padStart(2, "0")}s`;
};

const formatCallDate = (dateValue) => {
  if (!dateValue) return "-";
  const timestamp = Number(dateValue);
  const date = !isNaN(timestamp) ? new Date(timestamp) : new Date(dateValue);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CallActivityItem = ({ callData }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const isAnswered = !!callData.answeredAt;
  const isPending = callData.status === CALL_STATUSES.PENDING;
  const isAnsweredByVoicemail = callData.answeredByVoicemail === true;

  const [fetchRecordingUrl] = useLazyQuery(CALL_RECORDING_URL, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setLoadingRecording(false);
      const result = data?.callRecordingUrl;
      if (result?.url) {
        window.open(result.url, "_blank");
      } else if (result?.error) {
        notification.error({
          message: result.error,
          closeIcon: null,
        });
      }
    },
    onError: () => {
      setLoadingRecording(false);
      notification.error({
        message: t("ACTIVITIES.CALL.RECORDING_ERROR"),
        closeIcon: null,
      });
    },
  });

  const handleListenClick = () => {
    if (loadingRecording) return;
    setLoadingRecording(true);
    fetchRecordingUrl({ variables: { callId: callData.id } });
  };

  const title = isAnsweredByVoicemail
    ? t("ACTIVITIES.CALL.ANSWERED_BY_VOICEMAIL")
    : isPending
      ? t("ACTIVITIES.CALL.INITIATED")
      : isAnswered
        ? t("ACTIVITIES.CALL.ANSWERED")
        : t("ACTIVITIES.CALL.MISSED");

  const directionLabel =
    callData.direction === CALL_DIRECTIONS.INBOUND
      ? t("ACTIVITIES.CALL.INBOUND")
      : t("ACTIVITIES.CALL.OUTBOUND");

  const iconClass = isAnsweredByVoicemail
    ? "voicemail"
    : isPending
      ? "pending"
      : isAnswered
        ? "answered"
        : "missed";

  return (
    <div className="activities__log--item activities__log--call">
      <div className={classNames("icon", `call-icon--${iconClass}`)}>
        {isAnswered || isPending ? (
          <MdPhone size={12} />
        ) : (
          <MdPhoneMissed size={12} />
        )}
      </div>
      <div className="content">
        <div className="info">
          <div className="d-flex align--center gap-8">
            <span className="status">{title}</span>
            <span className="call-badge">AIRCALL</span>
          </div>
          <span className="call-details">
            {directionLabel} &middot; {callData.phoneNumber}
            {!isPending && callData.duration != null && (
              <>
                {" "}
                &middot; {t("ACTIVITIES.CALL.DURATION")} :{" "}
                {formatDuration(callData.duration)}
              </>
            )}
          </span>
          <div className="d-flex align--center gap-8">
            {callData.userName && (
              <span className="name">{callData.userName}</span>
            )}
            {callData.recordingAvailable && (
              <button
                className="call-listen-btn"
                onClick={handleListenClick}
                disabled={loadingRecording}
              >
                <MdHeadset size={12} />
                {loadingRecording
                  ? t("ACTIVITIES.CALL.LOADING")
                  : t("ACTIVITIES.CALL.LISTEN")}
              </button>
            )}
            {callData.aircallId && (
              <span className="call-aircall-id"># ac_{callData.aircallId}</span>
            )}
          </div>
        </div>
        <span className="date">
          {formatCallDate(callData.startedAt || callData.dialedAt)}
        </span>
      </div>
    </div>
  );
};

const StatusActivityItem = ({ activity }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const { status, date, author, type = TYPES.DEFAULT } = activity;
  const TRANSLATION = t("ACTIVITIES", { returnObjects: true });
  const { label, icon } = mapStatus(status, TRANSLATION);

  return (
    <div className="activities__log--item">
      <div
        className={classNames("icon", {
          error: type === TYPES.DANGER,
          primary: type === TYPES.DEFAULT,
        })}
      >
        {icon}
      </div>
      <div className="content">
        <div className="info">
          <span className="status">{label}</span>
          <span className="name">
            {!isEmpty(author) ? `${author?.firstname} ${author?.lastname}` : ""}
          </span>
        </div>
        <span className="date">{date ? formatDate(date) : "-"}</span>
      </div>
    </div>
  );
};

const ActivityItem = ({ activity }) => {
  if (activity.activityType === ACTIVITY_TYPES.CALL && activity.callData) {
    return <CallActivityItem callData={activity.callData} />;
  }
  return <StatusActivityItem activity={activity} />;
};

export default ActivityItem;
